import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCatalogo } from '../hooks/useCatalogo';
import { CatalogoCard, FiltrosPanel, ProductoDetalleModal } from '../components';
import { ProductoCatalogoPublico } from '../types';
import { useBolsas, BolsasPanel } from '../../cotizaciones';
import {
  CATALOGO_BASELINE_CARD_WIDTH_PX,
  CATALOGO_TARGET_CARD_MAX_WIDTH_PX,
  CATALOGO_PRODUCT_MIN_COL_WIDTH_PX,
  isCardWidthReductionCompliant,
} from '../utils/layoutRules';

export function CatalogoPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // SPEC-100 (RN-004) — auto-apertura de un solo uso: se lee una vez y se limpia el
  // query param inmediatamente para que un refresh posterior no reabra el modal.
  const autoOpenNuevaBolsa = searchParams.get('nuevaBolsa') === '1';

  useEffect(() => {
    if (autoOpenNuevaBolsa) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNuevaBolsa]);

  const {
    productos,
    loading,
    loadingMore,
    error,
    hasMore,
    filtros,
    filtrosDisponibles,
    loadingFiltros,
    proveedorIds,
    subcategoriasSeleccionadas,
    cambiarFiltro,
    limpiarFiltros,
    cargarMas,
    setProveedores,
    toggleSubcategoria,
    toggleCategoriaCompleta,
    limpiarSubcategorias,
  } = useCatalogo();

  const {
    bolsas,
    selectedBolsaId,
    loading: bolsasLoading,
    error: bolsasError,
    crearBolsa,
    agregarProductoABolsaActiva,
    guardarBorrador,
    handleSelectBolsa,
    eliminarProducto,
    eliminarBolsa,
  } = useBolsas();

  const [modalProducto, setModalProducto] = useState<ProductoCatalogoPublico | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handleCardClick = (producto: ProductoCatalogoPublico) => {
    setModalProducto(producto);
    setModalOpen(true);
  };

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        cargarMas();
      }
    },
    [hasMore, loadingMore, loading, cargarMas]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const widthReductionCompliant = isCardWidthReductionCompliant(
    CATALOGO_BASELINE_CARD_WIDTH_PX,
    CATALOGO_TARGET_CARD_MAX_WIDTH_PX
  );

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 3, lg: 4 } }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '230px 1fr 224px', lg: '252px 1fr 336px' },
        gridTemplateRows: { xs: 'auto auto auto auto', md: 'auto auto auto', lg: 'auto auto auto' },
        gap: { xs: 2, md: 2.5, lg: 3 },
        alignItems: 'start',
      }}>
        {/* ROW 1: Título + Búsqueda transversal (columnas 1-2) */}
        <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / 3', lg: '1 / 3' }, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#a0588e', marginBottom: 1 }}
              >
                Catálogo de Productos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Productos de todos los proveedores disponibles
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => navigate('/catalogo/matching')}
              sx={{
                backgroundColor: '#a0588e',
                color: 'white',
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { backgroundColor: '#8a4a78' },
              }}
            >
              Pendientes de matching
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Buscar por nombre, descripción o código..."
            value={filtros.texto}
            onChange={(e) => cambiarFiltro('texto', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              endAdornment: filtros.texto ? (
                <InputAdornment position="end">
                  <ClearIcon
                    onClick={() => cambiarFiltro('texto', '')}
                    sx={{ cursor: 'pointer', color: 'action.active' }}
                  />
                </InputAdornment>
              ) : null,
            }}
            inputProps={{ 'data-testid': 'catalogo-search-input' }}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
          />
        </Box>

        {/* Panel de Bolsas — columna 3, sticky */}
        <Box
          data-testid="bolsas-panel-wrapper"
          sx={{
            gridColumn: { xs: '1 / -1', md: '3 / 4', lg: '3 / 4' },
            gridRow: { xs: 'auto', md: 'auto', lg: '1 / -1' },
            position: { xs: 'relative', md: 'sticky', lg: 'sticky' },
            top: { xs: 'auto', md: 'calc(64px + 3rem + 80px)', lg: '64px' },
            maxHeight: { xs: 'auto', md: 'calc(100vh - 64px - 3rem - 80px)', lg: 'calc(100vh - 64px)' },
            overflow: { xs: 'visible', md: 'auto', lg: 'auto' },
            borderLeft: { xs: 'none', lg: '1px solid #e0e0e0' },
            borderTop: { xs: '1px solid #e0e0e0', lg: 'none' },
            paddingLeft: { xs: 0, lg: 2 },
            paddingTop: { xs: 2, lg: 0 },
            alignSelf: 'start',
          }}
        >
          <BolsasPanel
            bolsas={bolsas}
            selectedBolsaId={selectedBolsaId}
            loading={bolsasLoading}
            error={bolsasError}
            onSelectBolsa={handleSelectBolsa}
            onCreateBolsa={async (dto) => { await crearBolsa(dto); }}
            onSaveAsBorador={async (bolsaId) => { await guardarBorrador(bolsaId); }}
            onEliminarProducto={eliminarProducto}
            onDeleteBolsa={eliminarBolsa}
            autoOpenNuevaBolsa={autoOpenNuevaBolsa}
          />
        </Box>

        {/* Panel de Filtros — columna 1, sticky */}
        <Box data-testid="filtros-panel-wrapper" sx={{
          gridColumn: { xs: '1 / -1', md: '1 / 2', lg: '1 / 2' },
          position: { xs: 'relative', md: 'sticky', lg: 'sticky' },
          top: { xs: 'auto', md: 'calc(64px + 3rem + 80px)', lg: 'calc(64px + 3rem + 80px)' },
          maxHeight: { xs: 'auto', md: 'calc(100vh - 64px - 3rem - 80px)', lg: 'calc(100vh - 64px - 3rem - 80px)' },
          overflow: { xs: 'visible', md: 'auto', lg: 'auto' },
          alignSelf: 'start',
        }}>
          <FiltrosPanel
            filtros={filtros}
            filtrosDisponibles={filtrosDisponibles}
            loadingFiltros={loadingFiltros}
            proveedorIds={proveedorIds}
            subcategoriasSeleccionadas={subcategoriasSeleccionadas}
            productosCount={productos.length}
            onChange={cambiarFiltro}
            onSetProveedores={setProveedores}
            onToggleSubcategoria={toggleSubcategoria}
            onToggleCategoriaCompleta={toggleCategoriaCompleta}
            onLimpiarSubcategorias={limpiarSubcategorias}
            onLimpiar={limpiarFiltros}
          />
        </Box>

        {/* Grid de productos — columna 2 */}
        <Box sx={{
          gridColumn: { xs: '1 / -1', md: '2 / 3', lg: '2 / 3' },
          maxHeight: { xs: 'auto', lg: 'calc(100vh - 64px - 3rem - 80px)' },
          overflow: { xs: 'visible', lg: 'auto' },
        }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          )}

          {!loading && productos.length === 0 && !error && (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Typography variant="h6">Sin productos que coincidan con los filtros activos</Typography>
              <Typography variant="body2">Prueba ajustando o limpiando los filtros</Typography>
            </Box>
          )}

          {productos.length > 0 && (
            <Box
              data-testid="catalogo-products-grid"
              data-card-width-baseline={CATALOGO_BASELINE_CARD_WIDTH_PX}
              data-card-width-target={CATALOGO_TARGET_CARD_MAX_WIDTH_PX}
              data-width-reduction-ok={widthReductionCompliant ? 'true' : 'false'}
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${CATALOGO_PRODUCT_MIN_COL_WIDTH_PX}px, 1fr))`,
                gap: 2,
                overflowX: 'hidden',
              }}
            >
              {productos.map((producto, idx) => (
                <CatalogoCard
                  key={`${producto.id}-${idx}`}
                  producto={producto}
                  onClick={handleCardClick}
                  onAgregarClick={async (_prod, imagenUrl) => {
                    if (!selectedBolsaId) {
                      setSnackbarMessage('Crea una bolsa primero usando el botón "Crear nueva bolsa" en el panel lateral');
                      setSnackbarOpen(true);
                    } else {
                      try {
                        await agregarProductoABolsaActiva((producto.proveedorProductoId ?? producto.id!).toString(), 1, producto.nombre, imagenUrl ?? undefined);
                      } catch {
                        setSnackbarMessage('Error al agregar producto');
                        setSnackbarOpen(true);
                      }
                    }
                  }}
                />
              ))}
            </Box>
          )}

          <Box ref={sentinelRef} sx={{ height: 40, mt: 2 }} />

          {loadingMore && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!hasMore && productos.length > 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                — {productos.length} productos mostrados —
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <ProductoDetalleModal
        open={modalOpen}
        producto={modalProducto}
        onClose={() => setModalOpen(false)}
        selectedBolsaId={selectedBolsaId}
        onAgregar={async (prod, imagenUrl) => {
          if (!selectedBolsaId) {
            setSnackbarMessage('Crea una bolsa primero usando el botón "Crear nueva bolsa" en el panel lateral');
            setSnackbarOpen(true);
            return;
          }
          try {
            await agregarProductoABolsaActiva(
              (prod.proveedorProductoId ?? prod.id!).toString(),
              1,
              prod.nombre,
              imagenUrl ?? undefined
            );
            setSnackbarMessage(`"${prod.nombre}" agregado a la bolsa`);
            setSnackbarOpen(true);
          } catch {
            setSnackbarMessage('Error al agregar producto a la bolsa');
            setSnackbarOpen(true);
          }
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
}
